import { currencyFormatter } from "../../../../../../global/helpers";
import { formatDate } from "../../../../../../ui library/helpers/functions";

const EMPTY_VALUE = "N/A";
const isEmptyValue = (value) => value === null || value === undefined || value === "";

const formatCurrencyValue = (value) => {
  if (isEmptyValue(value)) {
    return EMPTY_VALUE;
  }
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return EMPTY_VALUE;
  }
  return currencyFormatter.format(numericValue);
};

const DetailRow = ({ label, value }) => {
  const content = isEmptyValue(value) ? EMPTY_VALUE : value;
  return (
    <div className="flex flex-col text-sm leading-5">
      <span className="font-semibold">{label}</span>
      <span className="break-words">{content}</span>
    </div>
  );
};

const normalizeListEntry = (entry) => {
  if (!entry && entry !== 0) {
    return null;
  }
  if (typeof entry === "string" || typeof entry === "number") {
    return { label: null, value: entry };
  }
  if (typeof entry === "object") {
    const { label = null } = entry;
    if ("value" in entry) {
      return { label, value: entry.value };
    }
    if (label !== null) {
      return { label, value: entry.value ?? null };
    }
  }
  return null;
};

const normalizeListItems = (items) =>
  Array.isArray(items) ? items.map((item) => normalizeListEntry(item)).filter(Boolean) : [];

const renderListEntryContent = (entry) => {
  const isObject = entry && typeof entry === "object";
  const label = isObject ? (entry.label ?? null) : null;
  const value = isObject ? entry.value : entry;
  const displayValue = isEmptyValue(value) ? EMPTY_VALUE : value;

  if (!label) {
    return <span className="break-words">{displayValue}</span>;
  }

  return (
    <span className="break-words">
      <span className="font-semibold">{label}:</span>{" "}
      <span className="font-normal">{displayValue}</span>
    </span>
  );
};

const MoreListItem = ({ restItems = [], label }) => {
  const items = Array.isArray(restItems) ? restItems : [];

  return (
    <div className="group relative inline-block">
      <span className="cursor-pointer text-xs text-blue-600 underline">More...</span>
      <div className="pointer-events-none absolute left-1/2 top-full z-10 mt-2 hidden min-w-[200px] -translate-x-1/2 rounded-md bg-white px-3 py-2 text-sm shadow-lg ring-1 ring-black/10 group-hover:block">
        <ul className="m-0 list-none p-0 text-gray-700">
          {items.map((entry, index) => (
            <li key={`${label}-rest-${index}`} className="break-words">
              {renderListEntryContent(entry)}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

const renderListRow = (label, items) => {
  const normalizedItems = normalizeListItems(items);
  if (!normalizedItems.length) {
    return <DetailRow key={label} label={label} value={EMPTY_VALUE} />;
  }

  const [firstItem, ...restItems] = normalizedItems;

  return (
    <div key={label} className="min-w-[200px] text-sm leading-5">
      <b>{label}:</b>
      <div className="mt-1 flex flex-wrap items-center gap-2 break-all">
        {items[0]?.value ?? renderListEntryContent(firstItem)}
        {restItems.length > 0 && <MoreListItem restItems={restItems} label={label} />}
      </div>
    </div>
  );
};

const itemsPerRowByHeader = {
  client_focused_header: 3,
  law_firm_focused_header: 2,
  provider_focused_header: 3,
  document_focused_header: 3,
};

const chunkItems = (items, itemsPerRow, maxRows = 2) =>
  items.reduce((rows, item, index) => {
    const rowIndex = Math.floor(index / itemsPerRow);
    if (rowIndex >= maxRows) {
      return rows;
    }
    if (!rows[rowIndex]) {
      rows[rowIndex] = [];
    }
    rows[rowIndex].push(item);
    return rows;
  }, []);

const padRowWithPlaceholders = (rowItems, itemsPerRow, rowIndex) => {
  const placeholders = Array.from({ length: Math.max(itemsPerRow - rowItems.length, 0) }).map(
    (_, idx) => ({
      label: `placeholder-${rowIndex}-${rowItems.length + idx}`,
      placeholder: true,
    })
  );
  return [...rowItems, ...placeholders];
};

const renderDetailItem = (item) => {
  if (item.placeholder) {
    return <div key={item.label} />;
  }
  if (item.render) {
    return <DetailRow key={item.label} label={item.label} value={item.render()} />;
  }
  if (item.type === "list") {
    return renderListRow(item.label, item.value);
  }
  return <DetailRow key={item.label} label={item.label} value={item.value} />;
};

const buildDetailItems = (headerType, formHeader, dobWithAge) => {
  const detailItemsByHeader = {
    client_focused_header: [
      { label: "Date of Birth", value: dobWithAge },
      { label: "Email", value: formHeader?.client_email ?? EMPTY_VALUE },
      { label: "Preferred Language", value: formHeader?.client_language ?? EMPTY_VALUE },
      { label: "Location", value: formHeader?.case_location ?? EMPTY_VALUE },
      { label: "Phone", value: formHeader?.client_phone ?? EMPTY_VALUE },
    ],
    law_firm_focused_header: [
      { label: "Law Firm Name", value: formHeader?.law_firm_name ?? EMPTY_VALUE },
      { label: "Law Firm Phone", value: formHeader?.law_firm_phone ?? EMPTY_VALUE },
      { label: "Law Firm Email", value: formHeader?.law_firm_email ?? EMPTY_VALUE },
      { label: "Legal Status", value: formHeader?.legal_status ?? EMPTY_VALUE },
    ],
    provider_focused_header: [
      { label: "Provider Name", value: formHeader?.provider_name ?? EMPTY_VALUE },
      {
        label: "Email(s)",
        type: "list",
        value: formHeader?.provider_emails,
      },
      { label: "EMR Type", value: formHeader?.provider_emr_type ?? EMPTY_VALUE },
      {
        label: "Phone(s)",
        type: "list",
        value: formHeader?.provider_phones,
      },
      {
        label: "EMR URL",
        render: () =>
          formHeader?.provider_emr_url ? (
            <a
              className="text-blue-600 underline"
              href={formHeader.provider_emr_url}
              target="_blank"
              rel="noreferrer"
            >
              {formHeader.provider_emr_url}
            </a>
          ) : (
            EMPTY_VALUE
          ),
      },
    ],
    document_focused_header: [
      { label: "File Name", value: formHeader?.file_name ?? EMPTY_VALUE },
      { label: "Document Type", value: formHeader?.document_type_label ?? EMPTY_VALUE },
      { label: "Document Source", value: formHeader?.document_source_label ?? EMPTY_VALUE },
      { label: "Provider", value: formHeader?.provider_name ?? EMPTY_VALUE },
      { label: "Referred From", value: formHeader?.referred_from_name ?? EMPTY_VALUE },
      { label: "Referred To", value: formHeader?.referred_to_name ?? EMPTY_VALUE },
    ],
  };

  const defaultDetailItems = [
    { label: "DOB", value: dobWithAge },
    { label: "Preferred Language", value: formHeader?.client_language ?? EMPTY_VALUE },
    { label: "Location", value: formHeader?.case_location ?? EMPTY_VALUE },
    {
      label: "DOA",
      render: () => formatDate(formHeader?.date_of_accident ?? "", "MM/dd/yyyy"),
    },
  ];

  return detailItemsByHeader[headerType] ?? defaultDetailItems;
};

export {
  DetailRow,
  formatCurrencyValue,
  renderListRow,
  itemsPerRowByHeader,
  chunkItems,
  padRowWithPlaceholders,
  renderDetailItem,
  buildDetailItems,
};
